/**
 * authStorage — keeps the admin's session intact when impersonating in a new tab.
 *
 * Strategy:
 *   - Normal login → token in `localStorage` (shared across tabs, persists)
 *   - Impersonation (new tab) → token in `sessionStorage` (per-tab, doesn't leak)
 *   - Reads always prefer sessionStorage so an impersonation tab beats the admin token
 */

export const getAuthToken = () =>
  sessionStorage.getItem('token') || localStorage.getItem('token');

export const isImpersonating = () =>
  sessionStorage.getItem('isImpersonating') === 'true';

export const getImpersonatedUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('impersonatedUser') || 'null');
  } catch {
    return null;
  }
};

/* For normal login — writes to localStorage so all admin tabs share it. */
export const setLoginToken = (token) => {
  localStorage.setItem('token', token);
};

export const removeLoginToken = () => {
  localStorage.removeItem('token');
};

/* For impersonation — writes only to the current tab's sessionStorage. */
export const setImpersonationSession = (token, user) => {
  sessionStorage.setItem('token', token);
  sessionStorage.setItem('isImpersonating', 'true');
  if (user) sessionStorage.setItem('impersonatedUser', JSON.stringify(user));
};

export const clearImpersonation = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('isImpersonating');
  sessionStorage.removeItem('impersonatedUser');
};

/* Centralised logout — clears whichever scope this tab is using. */
export const clearAuth = () => {
  if (isImpersonating()) {
    clearImpersonation();
  } else {
    removeLoginToken();
  }
};
